import { Link } from "react-router-dom";
import React, { useContext, useEffect, useState } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { submissionService, questionService, courseService } from '../../services/api';
import '../../styles/components/features/prev-questions.css';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { formatTimestamp } from '../../utils/dateUtils';
import { useUser } from "../../contexts/UserContext";
import Dropdown from 'react-bootstrap/Dropdown';
import { useCourse } from "../../contexts/CourseContext";


function PreviousQuestions() {
    const [submission, setSubmission] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const { user } = useUser();
    const [statusFilter, setStatusFilter] = useState("All");
    const [courseFilter, setCourseFilter] = useState("All");
    // const [courses, setCourses] = useState([]);
    const [sortingOrder, setSortingOrder] = useState("Newest to Oldest");
    const { selectedCourse, selectCourse, courses, setCourses } = useCourse();
    useEffect(() => {
        const fetchStudentSubmissions = async () => {
            if (!user || !user.studentID) return;
            setIsLoading(true);
            try {
                const recentSub = await submissionService.getMostRecentSubmissions(user.studentID);
                console.log("recentsub:", recentSub);
                const cachedSubmissions = JSON.parse(localStorage.getItem("userSubmissions") || "[]");
                const cachedCourses = localStorage.getItem("courses") || "[]";
                const newSubmissionCheck = cachedSubmissions.some(s =>
                    s.submissionID === recentSub.submissionID
                );
                if (cachedSubmissions.length !== 0 && cachedCourses.length !== 0 && newSubmissionCheck) {
                    setSubmission(cachedSubmissions);
                    setCourses(JSON.parse(cachedCourses));
                    setIsLoading(false);
                    return;
                }
                let submissions = [];
                let finalSubmissions = [];
                const subs = await submissionService.getMostRecentSubmissions(user.studentID);
                console.log("all subs:", subs);
                const courseList = await courseService.getCoursesInProgress();
                setCourses(courseList);
                submissions = subs;
                if (submissions.length !== 0) {
                    let qID = 0;
                    for (const sub of submissions) {
                        if (sub.qid !== qID) {
                            qID = sub.qid;
                            let courseCRN = selectedCourse?.CRN;
                            let courseObj = courseList.find(c => c.CRN === courseCRN);
                            finalSubmissions.push({
                                submissionID: sub.submissionID,
                                submissionTimestamp: sub.timestamp,
                                questionID: sub.qid,
                                status: sub.status === "Passed" ? "Correct" : "Incorrect",
                                courseID: courseCRN,
                                courseName: courseObj ? `${courseObj.name} : ${courseObj.semester}` : 'Unknown Course'
                            });
                        }
                    }
                }
                
                setSubmission(finalSubmissions);
                localStorage.setItem("userSubmissions", JSON.stringify(finalSubmissions));
                localStorage.setItem("courses", JSON.stringify(courses));
            } catch (error) {
                console.error('Error fetching submissions:', error);
                setSubmission([])
                localStorage.setItem("userSubmissions", JSON.stringify([]));
                // alert('Failed to load your submissions');
            } finally {
                setIsLoading(false);
            }
        };
        
        fetchStudentSubmissions();
    }, [user?.studentID, selectedCourse?.CRN]);
    
    let filteredSubmissions = [];
    if (Array.isArray(submission)) {
        filteredSubmissions = submission
            .filter((s) => {
                const matchesStatus = statusFilter === "All" || s.status === statusFilter;
                const matchesCourse = courseFilter === "All" || s.courseID === courseFilter;
                return (matchesStatus && matchesCourse);
            })
            .sort((date1, date2) => {
                const timestamp1 = new Date(date1.submissionTimestamp).getTime();
                const timestamp2 = new Date(date2.submissionTimestamp).getTime();
                return sortingOrder === "Newest to Oldest" ? timestamp2 - timestamp1 : timestamp1 - timestamp2;
            });
    }

    return(
        <div className="table-container">
            <div className="table-header">
                <h1 className="table-page-title">Your Previous Submissions</h1>
                <p className="table-page-subtitle">View and filter your past question submissions.</p>
            </div>
            
            <div className="table-wrapper">
                <div className="filter-controls">
                    <Dropdown onSelect={(status) => setStatusFilter(status)} className="filter-dropdown">
                        <Dropdown.Toggle variant="secondary" className="filter-button">
                            {statusFilter === "All" ? "Filter by Results" : `Filtering by: ${statusFilter}`}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item eventKey="All">All</Dropdown.Item>
                            <Dropdown.Item eventKey="Correct">Correct</Dropdown.Item>
                            <Dropdown.Item eventKey="Incorrect">Incorrect</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    
                    <Dropdown onSelect={(c) => setCourseFilter(c)} className="filter-dropdown">
                        <Dropdown.Toggle variant="secondary" className="filter-button">
                            {courseFilter === "All" ? "Filter by Course" : `Course: ${courses.find(course => course.CRN === courseFilter)?.name || courseFilter}`}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item eventKey="All">All</Dropdown.Item>
                            {courses.map((course, index) => (
                                <Dropdown.Item key={index} eventKey={course.CRN}>
                                    {course.name} {course.semester}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                    
                    <Dropdown onSelect={(order) => setSortingOrder(order)} className="filter-dropdown">
                        <Dropdown.Toggle variant="secondary" className="filter-button">
                            Sort: {sortingOrder}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item eventKey="Newest to Oldest">Newest to Oldest</Dropdown.Item>
                            <Dropdown.Item eventKey="Oldest to Newest">Oldest to Newest</Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </div>

                {isLoading ? (
                    <LoadingSpinner text="Loading your submissions..." />
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th>Time Stamp</th>
                                    <th>Question ID</th>
                                    <th>Course</th>
                                    <th>Code</th>
                                    <th style={{textAlign: 'center'}}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubmissions.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{textAlign: 'center', padding: '2rem', color: '#666'}}>
                                            No submissions found matching your filters.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredSubmissions.map((submissionItem, index) => (
                                        <tr key={index}>
                                            <td>{formatTimestamp(submissionItem.submissionTimestamp)}</td>
                                            <td>{submissionItem.questionID}</td>
                                            <td>{submissionItem.courseName}</td>
                                            <td>
                                                <Link to={`/question/${submissionItem.questionID}`} className="view-code-link">
                                                    View Code
                                                </Link>
                                            </td>
                                            <td style={{textAlign:'center'}}>
                                                {submissionItem.status === "Correct" ? (
                                                    <Badge pill bg="success" text="light">Correct</Badge>
                                                ) : (
                                                    <Badge pill bg="danger" text="light">Incorrect</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}
export default PreviousQuestions;